import React, {Component} from 'react'
import trans from "trans"
import {AxiosResponse} from "axios";
import store from "store";

import {
    Grid,
    GridCell,
    Spinner,
    Paginator,
    Section,
    css,
    DataHeader,
    DataTable,
    DataCell,
    DataBody,
    DataRow,
    Autocomplete,
    Theme,
} from "@bdfx/mergado-ui-kit"

import {Item} from "@bdfx/mergado-ui-kit/lib/components/Form/Autocomplete";
import {
    getFormatNamesLike,
    getAllFormatsSpecifications,
    getFormatSpecification
} from "app/helpers/api";
import {createToast} from "actions";
import {textToast} from "app/helpers/toast_factory";

interface Props {
    selectedProjectId: number
}

export type MergadoFormatSpecificationType = {
    id: string
    file_format: string
    global_schema: object
    item_schema: object
}

export type FormatSpecificationType = {
    format_id: string
    format_name: string
    image: string
    image_alternative: string
    item_id: string
    productno: string
    name_exact: string
    description: string
    price_vat: string
    ean: string
    url: string
    color: string
}

type FormatsListResponseType = {
    specifications: FormatSpecificationType[]
    page: number
    limit: number
    total_count: number
    max_page: number
}

interface State {
    autocompleteItems: Item[]
    searchString: string
    formatSpecification: MergadoFormatSpecificationType
    allFormats: FormatsListResponseType
    selectedFormat: string
}

class FormatSpecifications extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            autocompleteItems: [],
            searchString: null,
            selectedFormat: null,
            formatSpecification: null,
            allFormats: null,
        }
    }

    getFormats = (page:number, limit:number, searchString:string) => {
        getAllFormatsSpecifications(page, limit, searchString)
            .then((response: AxiosResponse<FormatsListResponseType>) => {
                this.setState({allFormats: response.data})
            })
            .catch(e => console.error('cannot get all formats', e))
    }

    componentDidMount() {
        this.getFormats(1, 10, null)
    }

    getSelectedFormatFromAPI = (selectedFormat: string) => {
        getFormatSpecification(selectedFormat)
            .then((response: AxiosResponse<MergadoFormatSpecificationType>) => {
                this.setState({formatSpecification: response.data})
            })
            .catch(e => console.error(e.response))
    }

    getAutocompleteItems = (searchString: string) => {
        getFormatNamesLike(searchString)
            .then((response: AxiosResponse<any>) => {
                const autocompleteItems = response.data.map(item => {
                    return {
                        value: item,
                        label: item,
                        text: item,
                    }
                })
                this.setState({autocompleteItems: autocompleteItems})
            })
            .catch(e => store.dispatch(createToast(textToast('error', JSON.stringify(e.response)))))
    }

    render() {
        const {formatSpecification, selectedFormat, allFormats, autocompleteItems, searchString} = this.state
        const paginator = allFormats && allFormats.max_page && allFormats.max_page > 1 ?
            <Paginator
                showFirstAndLast={true}
                showPrevAndNext={true}
                maxLinks={3}
                labelFirst={`««`}
                labelPrevious={`«`}
                labelNext={`»`}
                labelLast={`»»`}
                currentPage={allFormats.page}
                lastPage={allFormats.max_page}
                onPageChange={(page) => {
                    this.getFormats(page, allFormats.limit, this.state.searchString)
                }}
            /> : <span/>

        return (
            <StyledSection
                header={<Grid cols={'max-content 200px'} gap={'20px'}>
                    <GridCell className={'centering'}>{trans('mergado_formats')}:</GridCell>
                    <GridCell>
                    {allFormats && autocompleteItems &&
                    <Autocomplete
                        items={autocompleteItems}
                        onChange={(searchString) => {
                            this.setState({searchString: searchString})
                            this.getAutocompleteItems(searchString)
                            this.getFormats(1, allFormats.limit, searchString)
                        }}
                        onSelected={(formatName) => {
                            this.setState({selectedFormat: formatName})
                            this.getSelectedFormatFromAPI(formatName)
                        }}
                        value={searchString ? `${searchString}` : ''}
                        onClear={() => {
                            this.setState({
                                selectedFormat: null,
                                formatSpecification: null,
                                searchString: null
                            })
                            this.getFormats(1, allFormats.limit, null)
                        }}
                        placeholder={'format name'}
                    />}
                    </GridCell>
                </Grid>}
                suffix={<div><span>{selectedFormat && selectedFormat}</span> {searchString &&<span>({searchString})</span>} {allFormats?.max_page && <span>{allFormats.page}/{allFormats.max_page}</span>}</div>}
            >
                <Grid cols={'1fr 1fr'} gap={'10px'}>
                    <GridCell>
                        {paginator}
                        <DataTable>
                            <DataHeader>
                                <DataCell>{trans('format_name')}</DataCell>
                                <DataCell>{trans('element_img_url')}</DataCell>
                                <DataCell>{trans('element_alt_img_url')}</DataCell>
                                <DataCell>{trans('element_name')}</DataCell>
                                <DataCell>{trans('element_price_vat')}</DataCell>
                                <DataCell>{trans('element_item_id')}</DataCell>
                            </DataHeader>
                            <DataBody>
                                {allFormats && allFormats.specifications.map(format =>
                                <DataRow key={`format_${format.format_name}_${searchString}`}>
                                    <DataCell
                                        title={format.format_id}
                                        className={'clickable'}
                                        onClick={() => {
                                            this.setState({selectedFormat: format.format_name})
                                            this.getSelectedFormatFromAPI(format.format_name)
                                        }}><strong>{format.format_name}</strong></DataCell>
                                    <DataCell>{format.image}</DataCell>
                                    <DataCell>{format.image_alternative}</DataCell>
                                    <DataCell>{format.name_exact}</DataCell>
                                    <DataCell>{format.price_vat}</DataCell>
                                    <DataCell>{format.item_id}</DataCell>
                                </DataRow>
                                )}
                            </DataBody>
                        </DataTable>
                        {paginator}
                    </GridCell>
                    <GridCell>
                    {selectedFormat && formatSpecification &&
                    <StyledCode><code>{JSON.stringify(formatSpecification, null, "  ")}</code></StyledCode>
                    }
                    </GridCell>
                </Grid>
            </StyledSection>
        )
    }
}

const StyledCode = css('pre')({
    background: '#eeeeee',
    borderRadius: '8px',
    border: '1px solid #dddddd',
    height: '500px',
    overflow: 'auto',
    fontSize: '8px',
    code: {
        background: '#f6f6f6',
    }
})

const StyledSection = css(Section)({
    ' .m-suffix': {
        width: '250px',
    },
    ' .m-suffix .muk-form-group': {
        marginBottom: 0,
    },
    ' .centering': {
        alignSelf: 'center',
    }
})

export default FormatSpecifications